import { action, computed, makeObservable, observable } from 'mobx';
import { format, parseISO } from 'date-fns';
import { WinnerEvent } from '../../models';
import {
  WinnerEventRequest,
  winnerEventRequestSchema,
} from '../../models/schemas';
import { createValidationState, ValidationState } from '../../shared/validation';

type ModalType = 'add' | 'update' | undefined;

const DATE_FORMAT = 'yyyy-MM-dd';
const TIME_FORMAT = 'HH:mm:ss';

export class WinnerRequirementPageUiStore {
  @observable winnerEvents: WinnerEvent[] = [];

  @observable selectedWinnerEvent?: WinnerEvent;

  @observable winnerEventRequestBody?: WinnerEventRequest;

  @observable addOrEditEventModalType?: ModalType;

  @observable isShowDeleteEventModal: boolean = false;

  @observable isLoading: boolean = false;

  constructor() {
    makeObservable(this);
  }

  @computed
  get hasWinnerEvent(): boolean {
    return this.winnerEvents.length > 0;
  }

  @computed
  get winnerEventRequestValidation(): ValidationState<WinnerEventRequest> {
    return createValidationState(winnerEventRequestSchema.safeParse(this.winnerEventRequestBody));
  }

  @computed
  get isDeleteEventModalOpen(): boolean {
    return this.isShowDeleteEventModal && this.selectedWinnerEvent !== undefined;
  }

  @action
  setWinnerEvents(winnerEvents: WinnerEvent[]): void {
    this.winnerEvents = winnerEvents;
  }

  @action
  setSelectedWinnerEvent(winnerEvent?: WinnerEvent): void {
    this.selectedWinnerEvent = winnerEvent;
    if (!winnerEvent) {
      this.winnerEventRequestBody = undefined;
      return;
    }

    const initialValue: WinnerEventRequest = {
      displayName: winnerEvent.displayName,
      eventType: winnerEvent.eventType,
      eventDate: format(parseISO(winnerEvent.eventDate), DATE_FORMAT),
      startTime: winnerEvent.isTimeTBD ? winnerEvent.startTime : (winnerEvent.startTime ?? ''),
      endTime: winnerEvent.isTimeTBD ? winnerEvent.endTime : (winnerEvent.endTime ?? ''),
      isTimeTBD: winnerEvent.isTimeTBD,
      role: winnerEvent.role,
      numberOfAttendees: winnerEvent.numberOfAttendees,
    };

    this.winnerEventRequestBody = initialValue;
  }

  @action
  setWinnerEventRequestBody<K extends keyof WinnerEventRequest>(updatedField: { key: K; value: WinnerEventRequest[K] }): void {
    if (!this.winnerEventRequestBody) return;

    const { key, value } = updatedField;
    this.winnerEventRequestBody = {
      ...this.winnerEventRequestBody,
      [key]: value,
    };
  }

  @action
  setAddOrEditEventModalType(modalType?: ModalType): void {
    this.addOrEditEventModalType = modalType;
  }

  @action
  setShowDeleteEventModal(isShow: boolean): void {
    this.isShowDeleteEventModal = isShow;
  }

  @action
  setLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @action
  createWinnerEventRequestBody(): void {
    this.selectedWinnerEvent = undefined;
    this.winnerEventRequestBody = {
      displayName: '',
      eventType: 'Other',
      eventDate: format(new Date(), DATE_FORMAT),
      startTime: format(new Date(), TIME_FORMAT),
      endTime: format(new Date(), TIME_FORMAT),
      isTimeTBD: false,
      role: 'Winner',
      numberOfAttendees: 0,
    };
  }

  @action
  resetStates(): void {
    this.winnerEvents = [];
    this.selectedWinnerEvent = undefined;
    this.winnerEventRequestBody = undefined;
    this.addOrEditEventModalType = undefined;
    this.isShowDeleteEventModal = false;
    this.isLoading = false;
  }
}
